import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Observable, from, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { EncryptionService } from '../services/encryption.service';
import { AppConfigService } from '../config/app-config.service';

// ── Encrypted fields ──────────────────────────────────────────────────────────
// Only manager / delegate / additional-reviewer ratings and feedback are encrypted.
// Self-review data is stored and transmitted as plaintext.

const ENCRYPT_NUMERIC = new Set([
  'rating',                   // SubmitReviewDTO (non-self) + GoalReview
  'selfRating',               // CategoryDetailsDTO — DB column stored as encrypted text
  'managerRating',
  'reviewerRating',
  'additionalRating',
  'delegateRating',
  'additionalReviewerRating',
]);

const ENCRYPT_STRING = new Set([
  'selfFeedback',             // CategoryDetailsDTO — self_review_comment encrypted by migration
  'reviewComment',            // GoalReview feedback
  'managerFeedback',
  'managerComment',
  'managerReviewComment',
  'reviewerFeedback',
  'reviewerComment',
  'delegateFeedback',
  'additionalFeedback',
  'additionalReviewerComment',
]);

const ALL_ENCRYPTED = new Set([...ENCRYPT_NUMERIC, ...ENCRYPT_STRING]);

// ── Passthrough numeric fields ────────────────────────────────────────────────
// Reserved for future plaintext-string → Number conversions. selfRating was
// moved to ENCRYPT_NUMERIC; the looksEncrypted heuristic handles both
// encrypted values and pre-migration plaintext rows ("1"–"5") safely.
const NUMERIC_PASSTHROUGH = new Set<string>();

// ── Helpers ───────────────────────────────────────────────────────────────────

// Heuristic: AES-256-GCM output (12-byte IV + data + 16-byte tag) encodes to
// at least 40 base64 chars. Plain ratings ("1"–"5") and short comments are far
// shorter, so pre-migration rows and self-review values pass through correctly.
function looksEncrypted(value: unknown): value is string {
  if (typeof value !== 'string' || value.length < 40) return false;
  return /^[A-Za-z0-9+/]+=*$/.test(value);
}

async function transformObject(
  obj: unknown,
  enc: EncryptionService,
  encrypt: boolean,
): Promise<unknown> {
  if (Array.isArray(obj)) {
    return Promise.all(obj.map(item => transformObject(item, enc, encrypt)));
  }

  if (obj !== null && typeof obj === 'object') {
    const record = obj as Record<string, unknown>;

    // Detect SubmitReviewDTO: it has a 'reviewType' sibling field.
    // When reviewType === 'self', skip ALL field encryption for this object
    // so self ratings/comments are stored and returned as plaintext.
    const reviewType =
      typeof record['reviewType'] === 'string' ? record['reviewType'] : undefined;
    const isSelfReview = reviewType === 'self';

    const result: Record<string, unknown> = {};

    for (const [key, val] of Object.entries(record)) {
      // Null / empty — keep as-is regardless of field type.
      if (val == null || val === '') {
        result[key] = val;
        continue;
      }

      if (ALL_ENCRYPTED.has(key)) {
        if (encrypt) {
          // Self-review: never encrypt; all other review types: encrypt.
          result[key] = isSelfReview ? val : await enc.encrypt(String(val));
        } else if (looksEncrypted(val)) {
          // Ciphertext from backend — decrypt.
          const plain = await enc.decrypt(val);
          result[key] = ENCRYPT_NUMERIC.has(key) ? parseFloat(plain) : plain;
        } else {
          // Plaintext (pre-migration row or self-review stored value).
          result[key] =
            ENCRYPT_NUMERIC.has(key) && typeof val === 'string'
              ? parseFloat(val)
              : val;
        }
      } else if (key === 'comment' && reviewType && !isSelfReview && encrypt) {
        // SubmitReviewDTO.comment for non-self reviews: generic field name not
        // in ALL_ENCRYPTED, encrypted using reviewType as context signal.
        result[key] = await enc.encrypt(String(val));
      } else if (NUMERIC_PASSTHROUGH.has(key)) {
        // selfRating: not encrypted, but DB stores it as text → convert to Number.
        result[key] = typeof val === 'string' ? parseFloat(val) : val;
      } else {
        // All other fields: recurse into nested objects / arrays.
        result[key] = await transformObject(val, enc, encrypt);
      }
    }

    return result;
  }

  return obj;
}

function isHropsApi(url: string, config: AppConfigService): boolean {
  const base = config.get('apiBaseUrl') as string | undefined;
  if (base) return url.startsWith(base);
  // Empty apiBaseUrl → same-origin deployment; any relative path is a HROPS request.
  return url.startsWith('/');
}

// ── Interceptor ───────────────────────────────────────────────────────────────

export const encryptionInterceptor: HttpInterceptorFn = (req, next) => {
  const enc    = inject(EncryptionService);
  const config = inject(AppConfigService);

  if (!enc.isReady() || !isHropsApi(req.url, config)) {
    return next(req);
  }

  const encryptBody$ =
    req.body && typeof req.body === 'object' && !(req.body instanceof FormData)
      ? from(transformObject(req.body, enc, true)).pipe(
          map(encrypted => req.clone({ body: encrypted })),
        )
      : of(req);

  return (encryptBody$ as Observable<typeof req>).pipe(
    switchMap(modifiedReq => next(modifiedReq)),
    switchMap(event => {
      if (
        event instanceof HttpResponse &&
        event.body &&
        typeof event.body === 'object'
      ) {
        return from(transformObject(event.body, enc, false)).pipe(
          map(decrypted => event.clone({ body: decrypted })),
        );
      }
      return of(event);
    }),
  );
};
